import React from 'react';
import { CircleDot, Contrast, MousePointer2, Sun } from 'lucide-react';
import { useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { ErrorBanner } from '../../ui';
import { MiniDashboard, PreviewRow } from '../components/Previews';
import { Card, ChoiceCards, SectionHead, Toggle } from '../components/SettingsUI';
import { ACCENTS } from './AppearanceSection';
import useAutoSave from './useAutoSave';

const COLOR_BLIND = [
  ['none', 'Default (All colors)'], ['protanopia', 'Protanopia (red-blind)'], ['deuteranopia', 'Deuteranopia (green-blind)'],
  ['tritanopia', 'Tritanopia (blue-blind)'], ['achromatopsia', 'Achromatopsia (no colour)'],
];

/** Settings › Accessibility: text size, contrast, motion, focus outlines, cursor size and colour-blind support. */
export default function AccessibilitySection() {
  const { t } = useT();
  const { prefs, dark } = usePreferences();
  const { values, set, error } = useAutoSave('accessibility');
  const appearance = useAutoSave('appearance');
  const accent = (ACCENTS.find(([k]) => k === prefs.appearance.accent) || ACCENTS[0])[1];
  const textSize = (font_size) => appearance.set({ font_size });

  return (
    <>
      <SectionHead title={t('Accessibility')} subtitle={t('Customize the dashboard to make it easier and more comfortable for you to use.')} />
      <ErrorBanner message={error || appearance.error} />
      <Card title={t('Text Size')} subtitle={t('Adjust the text size across the application.')}>
        <ChoiceCards name={t('Text Size')} value={prefs.appearance.font_size} onChange={textSize} options={[
          { value: 'small', glyph: 'Aa', glyphStyle: { fontSize: 22 }, label: t('Small'), sub: t('Compact view') },
          { value: 'medium', glyph: 'Aa', glyphStyle: { fontSize: 27 }, label: t('Medium'), sub: t('Default size (Recommended)') },
          { value: 'large', glyph: 'Aa', glyphStyle: { fontSize: 31 }, label: t('Large'), sub: t('Larger text for easy reading') },
        ]} />
      </Card>

      <Card title={t('Display Contrast')} subtitle={t('Choose a contrast mode for better visibility.')}>
        <ChoiceCards name={t('Display Contrast')} value={values.contrast} onChange={(contrast) => set({ contrast })} options={[
          { value: 'normal', glyph: <Sun size={26} color="#f59e0b" />, label: t('Normal'), sub: t('Standard colors') },
          { value: 'high', glyph: <Contrast size={26} color="#1e3a8a" />, label: t('High Contrast'), sub: t('Stronger contrast for better visibility') },
          { value: 'extra', glyph: <CircleDot size={26} color="#000" />, label: t('Extra High Contrast'), sub: t('Maximum contrast (for low vision)') },
        ]} />
      </Card>

      <div className="st-two">
        <Card title={t('Reduce Motion')} subtitle={t('Minimize animations and moving effects.')}>
          <div className="st-inline-toggle">
            <Toggle checked={values.reduce_motion} onChange={(v) => set({ reduce_motion: v })} label={t('Enable reduced motion')} />
            <span><b>{t('Enable reduced motion')}</b><small>{t('Makes the interface simpler and easier to use.')}</small></span>
          </div>
        </Card>
        <Card title={t('Focus Indicators')} subtitle={t('Show clear focus outlines for better navigation.')}>
          <div className="st-inline-toggle">
            <Toggle checked={values.focus_indicators} onChange={(v) => set({ focus_indicators: v })} label={t('Enable focus indicators')} />
            <span><b>{t('Enable focus indicators')}</b><small>{t('Highlights the selected element (useful for keyboard navigation).')}</small></span>
          </div>
        </Card>
      </div>

      <div className="st-two">
        <Card title={t('Cursor Size')} subtitle={t('Increase cursor size for better visibility (especially useful on large screens).')}>
          <ChoiceCards name={t('Cursor Size')} layout="column" value={values.cursor} onChange={(cursor) => set({ cursor })} options={[
            { value: 'normal', glyph: <MousePointer2 size={20} fill="#111" />, label: t('Normal') },
            { value: 'large', glyph: <MousePointer2 size={28} fill="#111" />, label: t('Large') },
            { value: 'xl', glyph: <MousePointer2 size={38} fill="#111" />, label: t('Extra Large') },
          ]} />
        </Card>
        <Card title={t('Color Blind Support')} subtitle={t('Choose a color mode that is easier to distinguish.')}>
          <label className="st-field">
            <span className="st-control with-icon"><span className="st-rainbow" />
              <select value={values.color_blind} aria-label={t('Color Blind Support')} onChange={(e) => set({ color_blind: e.target.value })}>
                {COLOR_BLIND.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
              </select>
            </span>
            <small className="st-hint">{t('Status colours (answered, missed, pending) change to ones you can tell apart.')}</small>
          </label>
        </Card>
      </div>

      <Card title={t('Preview')} subtitle={t('See how the dashboard looks with your current accessibility settings.')}>
        <PreviewRow items={[
          { key: 'normal', drawing: <MiniDashboard variant={dark ? 'dark' : 'light'} accent={accent} />, label: t('Normal (Default)'), current: values.contrast === 'normal' && prefs.appearance.font_size !== 'large', onClick: () => set({ contrast: 'normal' }) },
          { key: 'contrast', drawing: <MiniDashboard variant="dark" contrast accent={accent} />, label: t('High Contrast'), current: values.contrast !== 'normal', onClick: () => set({ contrast: 'high' }) },
          { key: 'large', drawing: <MiniDashboard variant={dark ? 'dark' : 'system'} large accent={accent} />, label: t('Large Text'), current: prefs.appearance.font_size === 'large', onClick: () => textSize('large') },
        ]} />
      </Card>
    </>
  );
}
